import { useMemo } from "react";
import { DownloadOutlined, EyeOutlined, PaperClipOutlined } from "@ant-design/icons";

import { DocumentViewerModal } from "../../../features/beneficiary-document-reports/ui/DocumentViewerModal";
import { StatusPill } from "../../../shared/ui/status-pill";
import { SmartTable } from "../../../shared/ui/smart-table";
import { formatDate } from "../model/alertManagementConstants";
import { DetailBackButton, DetailViewRoot } from "./detail.styles";
import {
  HistorialDocActions,
  HistorialDocItem,
  HistorialDocLink,
  HistorialDocList,
  HistorialDocName,
  HistorialField,
  HistorialFieldLabel,
  HistorialFieldValue,
  HistorialNoDoc,
  HistorialScrollList,
  HistorialSectionCard,
  HistorialSectionTitle,
  HistorialSummaryGrid,
  HistorialTableWrapper,
} from "./historial.styles";

// A partir de este número de productos, la lista del resumen se vuelve scrollable.
const PRODUCTS_SCROLL_THRESHOLD = 10;

// Paleta de tono para el estado de la solicitud (mismo lenguaje visual del módulo).
const ESTADO_TOKENS = {
  neutral: { background: "#F3F4F6", border: "#D1D5DB", color: "#374151" },
  blue: { background: "#EEF2FF", border: "#C7D2FE", color: "#1D4ED8" },
  green: { background: "#E8F8EE", border: "#B7E4C7", color: "#04995B" },
  amber: { background: "#FFF4DB", border: "#FCDDA2", color: "#EA580C" },
  red: { background: "#FEE2E2", border: "#FCA5A5", color: "#991B1B" },
};

const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const estadoTone = (estado) => {
  const n = normalize(estado);
  if (n === "resuelta" || n === "finalizada" || n === "aprobada") return "green";
  if (n === "en subsanacion" || n === "pendiente") return "amber";
  if (n === "rechazada" || n === "anulada") return "red";
  if (n) return "blue";
  return "neutral";
};

const renderEstadoPill = (estado) => {
  const t = ESTADO_TOKENS[estadoTone(estado)];
  return (
    <StatusPill
      backgroundColor={t.background}
      borderColor={t.border}
      textColor={t.color}
      minHeight="28px"
      padding="4px 14px"
      fontSize="12px"
      fontWeight={800}
    >
      {estado || "—"}
    </StatusPill>
  );
};

// Muestra "---" para cualquier campo vacío o ausente.
const dash = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? "---" : text;
};

const buildTrazaColumns = ({ onViewDocument, onDownloadDocument }) => [
  { title: "Evento", dataIndex: "tipo", key: "evento", width: 200, render: dash },
  {
    title: "Observación",
    dataIndex: "observacion",
    key: "observacion",
    width: 300,
    render: dash,
  },
  {
    title: "Soporte documental",
    dataIndex: "soportes",
    key: "soportes",
    width: 240,
    render: (soportes = []) =>
      soportes.length ? (
        <HistorialDocList>
          {soportes.map((doc, i) => (
            <HistorialDocItem key={`${doc.nombre}-${i}`}>
              <HistorialDocName>
                <PaperClipOutlined /> {doc.nombre}
              </HistorialDocName>
              {doc.rutaArchivo && (
                <HistorialDocActions>
                  {doc.esPdf && (
                    <HistorialDocLink
                      type="button"
                      onClick={() => onViewDocument?.(doc)}
                    >
                      <EyeOutlined /> Ver
                    </HistorialDocLink>
                  )}
                  <HistorialDocLink
                    type="button"
                    onClick={() => onDownloadDocument?.(doc)}
                  >
                    <DownloadOutlined /> Descargar
                  </HistorialDocLink>
                </HistorialDocActions>
              )}
            </HistorialDocItem>
          ))}
        </HistorialDocList>
      ) : (
        <HistorialNoDoc>---</HistorialNoDoc>
      ),
  },
  { title: "Usuario", dataIndex: "usuario", key: "usuario", width: 220, render: dash },
  { title: "Rol", dataIndex: "rol", key: "rol", width: 140, render: dash },
  {
    title: "Fecha",
    dataIndex: "fecha",
    key: "fecha",
    width: 160,
    render: (value) => (String(value ?? "").trim() === "" ? "---" : formatDate(value)),
  },
  { title: "Estado", dataIndex: "estado", key: "estado", width: 140, render: dash },
];

export const AlertManagementHistorialView = ({
  record,
  data,
  loading,
  onBack,
  onViewDocument,
  onDownloadDocument,
  pdfViewer,
  onClosePdfViewer,
  onDownloadFromViewer,
}) => {
  const columns = useMemo(
    () => buildTrazaColumns({ onViewDocument, onDownloadDocument }),
    [onViewDocument, onDownloadDocument]
  );
  const eventos = data?.eventos ?? [];
  const resumen = data?.resumen ?? {};
  const productos = resumen.productos ?? [];
  const scrollProductos = productos.length > PRODUCTS_SCROLL_THRESHOLD;

  return (
    <DetailViewRoot>
      <DetailBackButton onClick={onBack}>Volver</DetailBackButton>

      <HistorialSectionCard bordered={false}>
        <HistorialSectionTitle>Resumen de la alerta</HistorialSectionTitle>
        <HistorialSummaryGrid>
          <HistorialField>
            <HistorialFieldLabel>ID Producto</HistorialFieldLabel>
            <HistorialScrollList $scroll={scrollProductos}>
              {productos.length ? (
                productos.map((p, i) => (
                  <HistorialFieldValue key={`id-${i}`}>
                    {p.idProducto || "—"}
                  </HistorialFieldValue>
                ))
              ) : (
                <HistorialFieldValue>—</HistorialFieldValue>
              )}
            </HistorialScrollList>
          </HistorialField>
          <HistorialField>
            <HistorialFieldLabel>Producto</HistorialFieldLabel>
            <HistorialScrollList $scroll={scrollProductos}>
              {productos.length ? (
                productos.map((p, i) => (
                  <HistorialFieldValue key={`nombre-${i}`}>
                    {p.nombre || "—"}
                  </HistorialFieldValue>
                ))
              ) : (
                <HistorialFieldValue>—</HistorialFieldValue>
              )}
            </HistorialScrollList>
          </HistorialField>
          <HistorialField>
            <HistorialFieldLabel>Tipo de gestión</HistorialFieldLabel>
            <HistorialFieldValue>{record?.tipoGestion || "—"}</HistorialFieldValue>
          </HistorialField>
        </HistorialSummaryGrid>
        <HistorialField>
          <HistorialFieldLabel>Estado actual</HistorialFieldLabel>
          <div>{renderEstadoPill(resumen.estadoActual)}</div>
        </HistorialField>
      </HistorialSectionCard>

      <HistorialSectionCard bordered={false}>
        <HistorialSectionTitle>Eventos de trazabilidad</HistorialSectionTitle>
        <HistorialTableWrapper>
          <SmartTable
            rowKey="rowKey"
            columns={columns}
            columnWidthMode="fixed"
            dataSource={eventos}
            loading={loading}
            showPagination={false}
            showToolbar={false}
            enableRowSelection={false}
            showColumnSettings={false}
            showTableResize={false}
            showReload={false}
            scroll={{ x: 1400 }}
            emptyText="Sin eventos de trazabilidad."
          />
        </HistorialTableWrapper>
      </HistorialSectionCard>

      <DocumentViewerModal
        isOpen={pdfViewer.isOpen}
        title="Visor de documento"
        subtitle={pdfViewer.title}
        documentUrl={pdfViewer.url}
        onClose={onClosePdfViewer}
        onDownload={onDownloadFromViewer}
      />
    </DetailViewRoot>
  );
};
